import Icon from 'components/Utils/NewIcon';
import React, { useEffect, useMemo, useState } from 'react';
import './style.scss';
import { UncontrolledCollapse } from 'components/Utils/Collapse';
import PerformanceUtils from 'services/PerformanceUtils';
import CalendarUtils from 'services/CalendarUtils';
import _ from 'lodash';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import SmallChart from 'components/HealthRecords/DiagnosticResults/SmallChart';
import UnreadBadge from 'components/Common/UnreadBadge';

const DEFAULT_ARRAY = [];

const DiagnosticResults = ({ document, allowCopy = true, normalInitialValue = true, onClick }) => {
  const dispatch = useAppDispatch();
  const documentId = document.id;

  const [documentsResults, setDocumentsResults] = useState([]);

  const hasDiagnosticResults = document.results_text?.length > 0;

  useEffect(() => {
    if (hasDiagnosticResults) {
      dispatch(api.Documents.getDocumentHistoricalResults({ documentId })).then((results) => {
        setDocumentsResults(results);
      });
    }
  }, [dispatch, documentId, hasDiagnosticResults]);

  const result = useMemo(() => {
    return documentsResults?.find(({ id }) => id === documentId)?.results;
  }, [documentsResults, documentId]);

  const flagged = useMemo(() => {
    return result?.filter(({ flag, test, result }) => !!flag && !!test && !!result);
  }, [result]);

  const nonFlagged = useMemo(() => {
    return result?.filter(({ flag, test, result }) => !flag && !!test && !!result);
  }, [result]);

  if (!result) return null;

  return (
    <div className="all-diagnostic-results">
      {flagged.length > 0 && (
        <UncontrolledCollapse
          titleElement={
            <span>
              Flagged Results{' '}
              <UnreadBadge unreadCount={flagged.length} style={{ background: '#2693d1' }} />
            </span>
          }
          initialValue={false}
        >
          <SingleDiagnosticResults
            results={flagged}
            documentsResults={documentsResults}
            allowCopy={allowCopy}
            onClick={onClick}
          />
        </UncontrolledCollapse>
      )}
      {nonFlagged.length > 0 && (
        <UncontrolledCollapse
          titleElement={
            <span>
              Normal Results{' '}
              <UnreadBadge unreadCount={nonFlagged.length} style={{ background: '#2693d1' }} />
            </span>
          }
          initialValue={normalInitialValue}
        >
          <SingleDiagnosticResults
            results={nonFlagged}
            documentsResults={documentsResults}
            onClick={onClick}
          />
        </UncontrolledCollapse>
      )}
    </div>
  );
};

const SingleDiagnosticResults = ({ results, documentsResults, allowCopy, onClick }) => {
  const [copyButtonClassName, setCopyButtonClassName] = useState('copy-button');

  function onClickedCopy() {
    let result = results
      .map((item) => {
        const { test, result, unit } = item;
        return `${test}: ${result} ${unit}`;
      })
      .join('\n');
    navigator.clipboard.writeText(result);
    setCopyButtonClassName('copy-button animated rubberBand primary text-primary blue');
    setTimeout(() => {
      setCopyButtonClassName('copy-button');
    }, 2000);
  }

  return (
    results.length > 0 && (
      <div className="diagnostic-results">
        {allowCopy && (
          <Icon
            src="copy"
            alt="Copy results"
            showTooltip={true}
            className={copyButtonClassName}
            width="20px"
            onClick={onClickedCopy}
          />
        )}
        {results.map((val, index) => (
          <DiagnosticResult
            key={index}
            value={val}
            documentsResults={documentsResults}
            onClick={onClick}
          />
        ))}
      </div>
    )
  );
};

function getIcons(value) {
  let { flag } = value;
  let icons;

  if (flag === 'L') {
    icons = ['arrow-down'];
  } else if (flag === 'LL') {
    icons = ['arrow-down', 'circle-exclamation'];
  } else if (flag === 'H') {
    icons = ['arrow-up'];
  } else if (flag === 'HH') {
    icons = ['arrow-up', 'circle-exclamation'];
  } else if (flag === 'A') {
    icons = ['exclamation-triangle'];
  }
  return icons;
}

const DiagnosticResult = ({ value, documentsResults, onClick }) => {
  const [tooltip, setTooltip] = useState();
  const [showTooltip, setShowTooltip] = useState(false);

  const unit = value.unit;
  const result = value.result;
  const referenceString = value.reference;
  const isText = value.result_type === 'text';

  let className = 'result-badge';
  if (value.flag) className += ' flag';
  if (isText) className += ' text-result';

  let icons = getIcons(value);
  icons = icons?.map((icon) => <Icon src={icon} />);

  const name = value.test_name;

  return (
    <div className={className} onClick={onClick ? () => onClick(value) : undefined}>
      <div className="data-wrapper">
        <div className="row1">
          <span className="test truncate" title={value.test}>
            {name}
          </span>
          <span className="result">
            {icons}
            {result} {unit}
          </span>
        </div>
        {!!referenceString && (
          <div className="row2">
            <span className="reference">Reference</span>
            <span className="range">
              {referenceString} {unit}
            </span>
          </div>
        )}
      </div>
      <div onMouseLeave={() => setShowTooltip(false)} onMouseEnter={() => setShowTooltip(true)}>
        <DiagnosticGraph
          value={value}
          documentsResults={documentsResults}
          setTooltip={setTooltip}
        />
      </div>
      <DefaultTooltip placement="right" open={showTooltip && !!tooltip} title={tooltip || ''}>
        <span />
      </DefaultTooltip>
    </div>
  );
};

const FLAG_COLOR = {
  L: '#66BDD0',
  LL: '#9ee1ef',
  H: '#d16f3e',
  HH: '#be480d',
  A: '#9c27b0',
  normal: '#156064',
};

const DiagnosticGraph = ({ value, documentsResults = DEFAULT_ARRAY, setTooltip }) => {
  const { test, result } = value;

  const data = useMemo(() => {
    let floatResult = parseFloat(result);
    if (isNaN(floatResult)) return [];

    let filteredData = _.uniqBy(
      documentsResults
        .map(({ results, date }) => {
          let values = _.uniqBy(
            results
              .filter((val) => val.test === test)
              .map((val) => {
                return {
                  value: parseFloat(val.result?.replace(/[^0-9.-]/g, '')),
                  date,
                  date_string: CalendarUtils.format(date),
                  color: FLAG_COLOR[val.flag || 'normal'],
                  data: val,
                };
              })
              .filter(({ value }) => !isNaN(value)),
            'date_service_string',
          );

          return { values, date };
        })
        .filter((vals) => vals.values.length > 0),
      'date',
    ).map((vals) => vals.values[0]);

    let values = PerformanceUtils.sortBy(filteredData, ['date'], false);
    let data = [];

    if (values.length > 1) {
      data = values.slice(-5).map(({ value, color, date_string, data }, index) => {
        let tooltip = `Value: ${value}\nDate: ${date_string}`;
        if (data.flag) tooltip += `\nFlag: ${data.flag}`;

        return {
          x: index,
          y: value,
          color,
          date_string,
          data,
          tooltip,
        };
      });
    }
    return data;
  }, [documentsResults, test, result]);

  return (
    <div className="small-chart-wrapper">
      {data.length > 0 && <SmallChart datasets={[{ data }]} onTooltip={setTooltip} />}
      {data.length === 0 && <div className="plot-wrapper placeholder">No historical data</div>}
    </div>
  );
};

export default DiagnosticResults;
